import {
  eventEmitterDefaultLogger,
  EventEmitterWithLogger,
} from '@surglogs/eventemitter'

import {
  type IAuthContextDataStore,
  AuthContextDataStoreEvent,
  type AuthDataFetcher,
  type AuthContextDataStoreListener,
} from '@surglogs-app/types'

import { AuthContextStore } from './AuthContextStore'
import { AuthTokenStore } from './AuthTokenStore'

export class AuthContextDataStore<TData>
  extends EventEmitterWithLogger<AuthContextDataStoreEvent>
  implements IAuthContextDataStore<TData>
{
  protected logger = eventEmitterDefaultLogger

  protected logPrefix = 'AuthContextDataStore'

  private data: TData | null = null

  private promiseCache: Record<number, Promise<TData>> = {}

  constructor(
    private fetcher: AuthDataFetcher<TData>,
    private tokenStore: AuthTokenStore,
    private contextStore: AuthContextStore,
  ) {
    super()

    this.tokenStore.onChange(({ token }) => {
      if (!token) {
        this.clearData()
      }
    })

    this.contextStore.onChange(({ userId }) => {
      this.fetch({ token: this.tokenStore.getToken(), userId })?.catch(
        // eslint-disable-next-line no-console
        console.error,
      )

      if (!userId) {
        this.clearData()
      }
    })
  }

  getData(): TData | null {
    return this.data
  }

  on(
    eventName: AuthContextDataStoreEvent,
    fn: AuthContextDataStoreListener<TData | null>,
  ) {
    return super.on(eventName, fn)
  }

  onChange(listener: AuthContextDataStoreListener<TData | null>): () => void {
    this.on(AuthContextDataStoreEvent.UPDATED, listener)
    return () => {
      this.off(AuthContextDataStoreEvent.UPDATED, listener)
    }
  }

  onValidChange(listener: (data: TData) => void) {
    return this.onChange((data) => (data ? listener(data) : null))
  }

  private fetch({
    token,
    userId,
  }: {
    token: string | null | undefined
    userId: number | null | undefined
  }): Promise<TData> | undefined {
    if (userId && token && !this.promiseCache[userId]) {
      this.emit(AuthContextDataStoreEvent.UPDATING, null)

      this.promiseCache[userId] = new Promise((resolve, reject) => {
        this.fetcher({ token })
          .then((value) => {
            delete this.promiseCache[userId]
            this.data = value
            this.emit(AuthContextDataStoreEvent.UPDATED, this.data)
            resolve(value)
          })
          .catch((error) => {
            delete this.promiseCache[userId]
            this.data = null
            this.emit(AuthContextDataStoreEvent.UPDATED, this.data)
            reject(error)
          })
      })
    }

    return userId ? this.promiseCache[userId] : undefined
  }

  fetchData() {
    return this.fetch({
      token: this.tokenStore.getToken(),
      userId: this.contextStore.getUserId(),
    })
  }

  isDataFetching(userId = this.contextStore.getUserId()): boolean {
    return Boolean(userId && this.promiseCache[userId])
  }

  clearData() {
    Object.keys(this.promiseCache).forEach((key) => {
      delete this.promiseCache[Number(key)]
    })
    this.promiseCache = {}

    if (this.data) {
      this.data = null
      this.emit(AuthContextDataStoreEvent.UPDATED, this.data)
    }

    return this
  }
}
